import React from "react";
import { motion } from "framer-motion";
import {
  HardHat, ArrowRight, FileSearch, Gauge, Trophy, TrendingUp,
  Check, ShieldCheck, Building2, Target, Sparkles,
  Cpu, Users, Package, Truck, Shield, Leaf,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import FoundingBanner from "@/components/FoundingBanner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1644221150167-fb4fafa7f411?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYmx1ZXByaW50fGVufDB8fHx8MTc4MjA3ODQ2M3ww&ixlib=rb-4.1.0&q=85";
const BLUEPRINT_IMG =
  "https://images.pexels.com/photos/4134179/pexels-photo-4134179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const trades = [
  { icon: HardHat, t: "General Construction" },
  { icon: Cpu, t: "Professional & IT Services" },
  { icon: Building2, t: "Facilities & Janitorial" },
  { icon: Users, t: "Staffing & Workforce" },
  { icon: Package, t: "Manufacturing & Supplies" },
  { icon: Truck, t: "Transportation & Logistics" },
  { icon: Shield, t: "Security Services" },
  { icon: Leaf, t: "Landscaping & Grounds" },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

const plans = [
  {
    id: "growth",
    name: "Growth",
    price: "$50",
    period: "/mo",
    trial: true,
    audience: "Small contractors & owner-operators",
    tagline: "Everything you need to find real opportunities, score them instantly, and win your first federal contract — without hiring a business developer.",
    features: ["5-day free trial — full access", "Unlimited opportunities", "AI Qualification & Win Scores", "Growth Roadmap", "Score Any Bid"],
    cta: "Start 5-day free trial",
    highlight: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$100",
    period: "/mo",
    audience: "Growing contractors with a BD team",
    tagline: "Full AI bid-package analysis, competitive intelligence on who else is winning, and a shared bid pipeline so your team stops emailing spreadsheets.",
    features: ["Everything in Growth", "AI Bid Triage (RFP analysis)", "Competitive Intelligence", "Bid CRM pipeline"],
    cta: "Choose Pro",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$999+",
    period: "/mo",
    audience: "Large contractors & multi-office orgs",
    tagline: "Custom AI limits, unlimited team seats, SSO, API access, and a dedicated success manager who knows your pipeline by name.",
    features: ["Everything in Pro", "Team seats & roles", "Dedicated success manager", "API access"],
    cta: "Contact Sales",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const go = () => (window.location.href = user ? "/dashboard" : "/signup");
  const getScore = () => (user ? (window.location.href = "/dashboard") : (window.location.href = "/readiness"));

  return (
    <div className="min-h-screen bg-[#fafdfb] text-slate-900 font-sans antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-900 flex items-center justify-center">
              <HardHat className="h-4 w-4 text-lime-300" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900 transition-colors">Features</a>
            <a href="#how" className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="/platform" className="hover:text-slate-900 transition-colors">Why BidMatchAI</a>
            <a href="/for-agencies" className="hover:text-slate-900 transition-colors">For Issuing Organizations</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
          </nav>
          <button
            onClick={() => (window.location.href = user ? "/dashboard" : "/login")}
            data-testid="nav-login-button"
          className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-md px-5 py-2.5 text-sm font-medium transition-all shadow-sm hover:-translate-y-0.5"
          >
            {user ? "Dashboard" : "Sign in"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-[0.06]" />
          <div className="absolute -top-32 right-0 h-[480px] w-[480px] rounded-full bg-lime-300/40 blur-[120px]" />
          <div className="absolute top-40 -left-20 h-[420px] w-[420px] rounded-full bg-emerald-400/30 blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-lime-500" /> AI Contractor Intelligence
            </motion.div>
            <motion.h1 {...fadeUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Know Before<br />You Bid.
            </motion.h1>
            <motion.p {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              The Credit Karma for contractors. BidMatchAI scores your win probability, surfaces qualified opportunities, and tells you exactly what's missing — before you waste time on bids you can't win.
            </motion.p>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }} className="mt-9 flex flex-col sm:flex-row gap-3">
              <button onClick={go} data-testid="hero-start-trial-button" className="group bg-emerald-900 text-white hover:bg-emerald-800 rounded-md px-7 py-3.5 font-medium transition-all shadow-sm hover:-translate-y-0.5 inline-flex items-center justify-center gap-2">
                Start your 5-day free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={getScore} data-testid="hero-get-score-button" className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-md px-7 py-3.5 font-medium transition-all shadow-sm inline-flex items-center justify-center">
                Get your free contractor readiness score
              </button>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.3 }} className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-slate-500">
              <span data-testid="early-access-badge" className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3.5 py-1.5 font-semibold text-emerald-800">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-500" />
                </span>
                Early access — now onboarding contractors
              </span>
            </motion.div>
          </div>

          {/* Floating UI cards */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-5 relative">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Contractor Readiness</p>
              <div className="mt-3 flex items-end gap-3">
                <span className="font-data text-6xl font-semibold tracking-tight text-slate-900">842</span>
                <span className="mb-2 text-emerald-600 text-sm font-semibold">Bid-Ready</span>
              </div>
              <div className="mt-4 h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: "84%" }} />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[["Win Probability", "78%", "text-emerald-700"], ["Qualification", "91%", "text-emerald-600"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                    <p className="text-xs text-slate-500">{l}</p>
                    <p className={`font-data text-2xl font-medium ${c}`}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:flex bg-emerald-950 text-white rounded-xl shadow-xl p-4 items-center gap-3">
              <FileSearch className="h-5 w-5 text-lime-400" />
              <div>
                <p className="text-xs text-emerald-300/80">RFP Triage</p>
                <p className="text-sm font-semibold">Recommendation: Bid</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value props bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">What you get</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Four questions every contractor must answer before bidding.</h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div {...fadeUp} className="md:col-span-8 relative overflow-hidden bg-emerald-950 text-white rounded-2xl p-8 min-h-[280px] flex flex-col justify-between">
            <img src={BLUEPRINT_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
            <div className="relative">
              <FileSearch className="h-8 w-8 text-lime-400" />
              <h3 className="mt-4 font-heading text-2xl font-bold">AI Bid Triage & RFP Analysis</h3>
              <p className="mt-3 text-emerald-100/80 max-w-md">Upload any RFP, RFQ, or IFB. Our AI extracts deadlines, requirements, risks, and compliance needs — then recommends <span className="text-lime-300 font-semibold">Bid</span>, <span className="text-amber-300 font-semibold">Bid with Caution</span>, or <span className="text-red-300 font-semibold">Avoid</span>.</p>
            </div>
            <div className="relative mt-6 flex gap-2 flex-wrap">
              {["Deadlines", "Requirements", "Risk flags", "Compliance"].map((t) => (
                <span key={t} className="text-xs font-data bg-white/10 border border-white/10 rounded-full px-3 py-1">{t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 bg-white border border-slate-200 rounded-2xl p-8 flex flex-col">
            <Gauge className="h-8 w-8 text-emerald-600" />
            <h3 className="mt-4 font-heading text-xl font-semibold">Readiness Score</h3>
            <p className="mt-2 text-slate-600 text-sm flex-1">A dynamic 0–1000 score across licensing, bonding, insurance, safety, and experience.</p>
            <span className="font-data text-4xl font-medium mt-4 text-slate-900">0–1000</span>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-4 bg-white border border-slate-200 rounded-2xl p-8 flex flex-col">
            <Trophy className="h-8 w-8 text-amber-500" />
            <h3 className="mt-4 font-heading text-xl font-semibold">Win Probability</h3>
            <p className="mt-2 text-slate-600 text-sm flex-1">AI estimates your odds against the field using historical award data and your profile.</p>
          </motion.div>

          <motion.div {...fadeUp} className="md:col-span-8 bg-white border border-slate-200 rounded-2xl p-8 flex flex-col sm:flex-row gap-6 items-start">
            <TrendingUp className="h-8 w-8 text-emerald-600 shrink-0" />
            <div>
              <h3 className="font-heading text-xl font-semibold">Growth Roadmap</h3>
              <p className="mt-2 text-slate-600 text-sm">Get a prioritized action plan to qualify for larger contracts — increase bonding capacity, earn certifications, and build the experience that wins bigger public-sector work.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Who it's for</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Built for the businesses that power government.</h2>
            <p className="mt-3 text-slate-600">From construction to IT, facilities to professional services — BidMatchAI maps your work to the federal, state, and local opportunities you're qualified to win, wherever you operate.</p>
            <p className="mt-4 inline-flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3" data-testid="inclusive-line">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Including small, women-owned (WOSB), service-disabled veteran-owned (SDVOSB), HUBZone, minority-owned, and 8(a) businesses — we surface the set-asides you're eligible to win.</span>
            </p>
          </motion.div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {trades.map((tr) => (
              <motion.div key={tr.t} {...fadeUp} data-testid={`trade-${tr.t}`} className="group rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-3 hover:border-emerald-300 hover:shadow-sm transition-all">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <tr.icon className="h-5 w-5 text-emerald-700" />
                </div>
                <span className="text-sm font-medium text-slate-800">{tr.t}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-emerald-50/50 border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.h2 {...fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">From profile to winning bid in three steps.</motion.h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: Building2, n: "01", t: "Build your profile", d: "Add licensing, bonding, insurance, safety record, and experience. We compute your Readiness Score instantly." },
              { icon: Target, n: "02", t: "Match opportunities", d: "We pull live public contract opportunities in your trade and score how qualified you are for each." },
              { icon: ShieldCheck, n: "03", t: "Bid with confidence", d: "Run RFPs through AI triage, track pursuits in your CRM, and follow your roadmap to bigger contracts." },
            ].map((s) => (
              <motion.div key={s.n} {...fadeUp} className="bg-white border border-emerald-100 rounded-2xl p-8">
                <div className="flex items-center justify-between">
                  <s.icon className="h-7 w-7 text-emerald-700" />
                  <span className="font-data text-sm text-slate-300 font-medium">{s.n}</span>
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold">{s.t}</h3>
                <p className="mt-2 text-slate-600 text-sm">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product preview */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">See it in action</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your entire bid pipeline, scored at a glance.</h2>
        </motion.div>

        <motion.div {...fadeUp} className="mt-12 rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-emerald-900/5 overflow-hidden">
          {/* window bar */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="h-3 w-3 rounded-full bg-red-300" />
            <span className="h-3 w-3 rounded-full bg-amber-300" />
            <span className="h-3 w-3 rounded-full bg-emerald-300" />
            <span className="ml-3 text-xs font-data text-slate-400">app.bidmatchai.io / opportunities</span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 p-6 md:p-8 bg-[#fafdfb]">
            {/* readiness */}
            <div className="lg:col-span-4 rounded-xl border border-emerald-100 bg-white p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Readiness</p>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-data text-5xl font-semibold text-slate-900">842</span>
                <span className="mb-1.5 text-emerald-600 text-sm font-semibold">Bid-Ready</span>
              </div>
              <div className="mt-4 h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: "84%" }} />
              </div>
              <div className="mt-6 space-y-3">
                {[["Bonding", 82], ["Certifications", 90], ["Safety (EMR)", 76]].map(([l, w]) => (
                  <div key={l}>
                    <div className="flex justify-between text-xs text-slate-500"><span>{l}</span><span className="font-data">{w}%</span></div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-lime-400" style={{ width: `${w}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
            {/* opportunity list */}
            <div className="lg:col-span-8 rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-900">Matched opportunities</p>
                <span className="text-xs font-data text-slate-400">Live · Updated daily</span>
              </div>
              <div className="mt-4 divide-y divide-slate-100">
                {[
                  { t: "HVAC Modernization — Federal Building", a: "GSA", amt: "$2.4M", win: 78, rec: "Bid", c: "bg-emerald-100 text-emerald-700" },
                  { t: "Electrical Infrastructure Upgrade", a: "Dept. of Defense", amt: "$5.8M", win: 61, rec: "Caution", c: "bg-amber-100 text-amber-700" },
                  { t: "Bridge Concrete Restoration", a: "DOT", amt: "$12.5M", win: 34, rec: "Avoid", c: "bg-red-100 text-red-600" },
                  { t: "VA Medical Center Roofing", a: "Veterans Affairs", amt: "$1.85M", win: 84, rec: "Bid", c: "bg-emerald-100 text-emerald-700" },
                ].map((o) => (
                  <div key={o.t} className="flex items-center justify-between gap-4 py-3.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{o.t}</p>
                      <p className="text-xs text-slate-400">{o.a} · {o.amt}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="font-data text-lg font-semibold text-slate-900 leading-none">{o.win}%</p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">win prob</p>
                      </div>
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${o.c}`}>{o.rec}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Pricing</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Plans that scale with your pipeline.</h2>
          <p className="mt-4 text-base text-slate-600">One won contract pays for <span className="font-semibold text-emerald-800">years</span> of BidMatchAI. Pick the tier that matches how aggressively you're bidding.</p>
        </motion.div>
        <div className="mt-10"><FoundingBanner /></div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 border border-slate-200 rounded-2xl overflow-hidden">
          {plans.map((p, i) => (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`p-8 border-slate-200 ${i !== 0 ? "border-t md:border-t-0 md:border-l" : ""} ${p.highlight ? "bg-emerald-950 text-white" : "bg-white"} flex flex-col`}
            >
              {p.highlight && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="self-start text-xs font-bold uppercase tracking-wider bg-lime-400 text-emerald-950 rounded-full px-3 py-1">Most Popular</span>
                  <a href="#pricing" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="founding-banner"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }} data-testid="founding-ribbon" className="self-start text-xs font-bold uppercase tracking-wider bg-emerald-800 text-lime-300 rounded-full px-3 py-1 inline-flex items-center gap-1 hover:bg-emerald-700 transition-colors">
                    <Sparkles className="h-3 w-3" /> Founding · $39/mo
                  </a>
                </div>
              )}
              <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
              {p.audience && (
                <p className={`text-xs font-medium mt-1 ${p.highlight ? "text-lime-300" : "text-emerald-700"}`}>{p.audience}</p>
              )}
              <div className="mt-3 flex items-end gap-1">
                <span className="font-data text-4xl font-semibold">{p.price}</span>
                <span className={`mb-1 text-sm ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>{p.period}</span>
              </div>
              {p.trial && <p className="mt-1 text-xs text-lime-300 font-medium">Free for 5 days, then $50/mo</p>}
              {p.tagline && (
                <p className={`mt-4 text-sm leading-relaxed ${p.highlight ? "text-emerald-100/80" : "text-slate-600"}`}>{p.tagline}</p>
              )}
              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.highlight ? "text-lime-400" : "text-emerald-600"}`} />
                    <span className={p.highlight ? "text-emerald-100" : "text-slate-600"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={go}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-8 rounded-md px-5 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 ${
                  p.highlight ? "bg-lime-400 text-emerald-950 hover:bg-lime-300" : "bg-emerald-900 text-white hover:bg-emerald-800"
                }`}
              >
                {p.cta}
              </button>
            </div>
          ))}
        </div>
        <motion.p {...fadeUp} className="mt-6 text-center text-sm text-slate-500">5-day free trial on Growth · Card required · Auto-converts to Growth after the trial · Cancel anytime from the self-serve billing portal.</motion.p>
      </section>

      {/* Footer CTA */}
      <footer className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <h2 className="font-heading font-extrabold tracking-tighter leading-none text-[14vw] md:text-[9vw]">Let's Build.</h2>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/10 pt-8">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-lime-400 flex items-center justify-center">
                <HardHat className="h-4 w-4 text-emerald-950" strokeWidth={2.2} />
              </div>
              <span className="font-heading font-bold">BidMatchAI</span>
            </div>
            <button onClick={go} data-testid="footer-cta-button" className="bg-lime-400 hover:bg-lime-300 text-emerald-950 rounded-md px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 inline-flex items-center gap-2">
            Start your 5-day free trial <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-8 text-sm text-emerald-300/70">© 2026 BidMatchAI · The operating system for contractor growth.</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-emerald-300/70">
            <a href="/terms" className="hover:text-lime-300" data-testid="footer-terms">Terms of Service</a>
            <a href="/privacy" className="hover:text-lime-300" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/disclaimer" className="hover:text-lime-300" data-testid="footer-disclaimer">Disclaimer</a>
            <a href="/refund-policy" className="hover:text-lime-300" data-testid="footer-refund">Refund &amp; Cancellation</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
